/**
 * The single, fail-closed eligibility gate for canonical part pages.
 *
 * Both the scoped SSR renderer and every part sitemap import this function.
 * Keeping the decision pure makes it straightforward to regression test and
 * prevents a page from being indexable in one surface but excluded in another.
 */
export const CANONICAL_SITE_URL = "https://www.control-exchange.com";

export interface PartIndexabilitySignals {
  publicationApproved: boolean;
  normalizedManufacturer: string | null | undefined;
  normalizedMpn: string | null | undefined;
  manufacturerEvidence: boolean;
  administrativelyReviewed: boolean;
  independentExactSourceIds: readonly string[];
  hasCurrentExactOffer: boolean;
  hasSubstantiveResearch: boolean;
  hasManufacturerLifecycle: boolean;
  hasCuratedAlternative: boolean;
}

export interface PartIndexabilityDecision {
  indexable: boolean;
  publicationApproved: boolean;
  identityComplete: boolean;
  identitySupported: boolean;
  hasUsefulSignal: boolean;
  reasons: string[];
}

export function normalizeIdentityValue(value: unknown): string {
  return String(value || "")
    .normalize("NFKC")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

export function uniqueIndependentSources(sourceIds: readonly string[]): string[] {
  return [...new Set(sourceIds.map(normalizeIdentityValue).filter(Boolean))];
}

export function evaluatePartIndexability(
  signals: PartIndexabilitySignals,
): PartIndexabilityDecision {
  const identityComplete = Boolean(
    normalizeIdentityValue(signals.normalizedManufacturer)
      && normalizeIdentityValue(signals.normalizedMpn),
  );
  const exactSourceCount = uniqueIndependentSources(signals.independentExactSourceIds).length;
  const identitySupported = Boolean(
    signals.manufacturerEvidence
      || signals.administrativelyReviewed
      || exactSourceCount >= 2,
  );
  const hasUsefulSignal = Boolean(
    signals.hasCurrentExactOffer
      || signals.hasSubstantiveResearch
      || signals.hasManufacturerLifecycle
      || signals.hasCuratedAlternative,
  );

  const reasons: string[] = [];
  if (!signals.publicationApproved) reasons.push("not_published_for_acquisition");
  if (!identityComplete) reasons.push("missing_normalized_identity");
  if (!identitySupported) reasons.push("identity_not_supported");
  if (!hasUsefulSignal) reasons.push("no_useful_signal");

  return {
    indexable: signals.publicationApproved && identityComplete && identitySupported && hasUsefulSignal,
    publicationApproved: signals.publicationApproved,
    identityComplete,
    identitySupported,
    hasUsefulSignal,
    reasons,
  };
}

function researchRecord(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value !== "string") return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : {};
  } catch {
    return {};
  }
}

function meaningfulResearchValue(value: unknown): boolean {
  const text = String(value ?? "").replace(/\s+/g, " ").trim();
  if (!text) return false;
  return !/^(?:n\/?a|none|unknown|pending|tbd|not available|not found|-)$/i.test(text);
}

/** Prevent completed placeholders and one-field stubs from opening the gate. */
export function hasSubstantivePartResearch(research: Record<string, unknown> | null | undefined): boolean {
  if (!research || research.research_status !== "completed") return false;
  const summary = String(research.summary || research.research_summary || research.description || "")
    .replace(/\s+/g, " ")
    .trim();
  const wordCount = summary.split(/\s+/).filter(Boolean).length;
  if (summary.length >= 100 && wordCount >= 12 && meaningfulResearchValue(summary)) return true;
  const specifications = {
    ...researchRecord(research.specifications),
    ...researchRecord(research.technical_specs),
  };
  const meaningfulSpecifications = Object.entries(specifications).filter(([name, value]) => (
    meaningfulResearchValue(name) && meaningfulResearchValue(value)
  ));
  return meaningfulSpecifications.length >= 3;
}

export function manufacturersAgree(
  observedManufacturer: unknown,
  acceptedNormalizedManufacturers: readonly string[],
): boolean {
  const observed = normalizeIdentityValue(observedManufacturer);
  if (!observed) return false;
  return acceptedNormalizedManufacturers
    .map(normalizeIdentityValue)
    .filter(Boolean)
    .includes(observed);
}

const MANUFACTURER_EVIDENCE_DOMAINS: Record<string, readonly string[]> = {
  SIEMENS: ["siemens.com"],
  ALLENBRADLEY: ["rockwellautomation.com", "ab.com"],
  ROCKWELLAUTOMATION: ["rockwellautomation.com", "ab.com"],
  SCHNEIDERELECTRIC: ["se.com", "schneider-electric.com"],
  ABB: ["abb.com"],
  OMRON: ["omron.com", "automation.omron.com"],
  MITSUBISHI: ["mitsubishielectric.com", "mitsubishielectric.co.uk"],
  MITSUBISHIELECTRIC: ["mitsubishielectric.com", "mitsubishielectric.co.uk"],
  PHOENIXCONTACT: ["phoenixcontact.com"],
  FESTO: ["festo.com"],
  SICK: ["sick.com"],
  PILZ: ["pilz.com"],
  DANFOSS: ["danfoss.com"],
  BECKHOFF: ["beckhoff.com"],
  KEYENCE: ["keyence.com", "keyence.co.uk"],
  PROFACE: ["pro-face.com"],
  EATON: ["eaton.com"],
  MOXA: ["moxa.com"],
  HONEYWELL: ["honeywell.com"],
  YASKAWA: ["yaskawa.com"],
  WAGO: ["wago.com"],
  BOSCHREXROTH: ["boschrexroth.com"],
  IFM: ["ifm.com"],
  TURCK: ["turck.com"],
  FANUC: ["fanuc.com", "fanuc.eu"],
};

/** A submitted datasheet URL is evidence only when it is on a known OEM domain. */
export function isVerifiedManufacturerEvidenceUrl(
  value: unknown,
  normalizedManufacturer: unknown,
): boolean {
  const manufacturer = normalizeIdentityValue(normalizedManufacturer);
  const allowed = MANUFACTURER_EVIDENCE_DOMAINS[manufacturer];
  if (!allowed?.length) return false;
  try {
    const url = new URL(String(value || ""));
    if (url.protocol !== "https:") return false;
    const hostname = url.hostname.toLowerCase().replace(/^www\./, "");
    return allowed.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
  } catch {
    return false;
  }
}

/**
 * Establish identity evidence only from a service-ingested OEM lifecycle row.
 * `submittedDatasheetUrl` is accepted to make the trust boundary explicit but
 * is deliberately ignored: seller-editable part metadata cannot open the gate.
 */
export function hasManufacturerBackedLifecycleEvidence(input: {
  normalizedManufacturer: unknown;
  acceptedNormalizedManufacturers: readonly string[];
  lifecycleStatus: unknown;
  lifecycleManufacturer: unknown;
  lifecycleEvidenceUrl: unknown;
  submittedDatasheetUrl?: unknown;
}): boolean {
  return Boolean(
    input.lifecycleStatus
      && input.lifecycleManufacturer
      && manufacturersAgree(input.lifecycleManufacturer, input.acceptedNormalizedManufacturers)
      && isVerifiedManufacturerEvidenceUrl(input.lifecycleEvidenceUrl, input.normalizedManufacturer),
  );
}

function normalizedRetailerName(value: unknown): string {
  const normalized = normalizeIdentityValue(value)
    .replace(/(?:ELECTRONICS|ELECTRONIC|DISTRIBUTORS?|DISTRIBUTION|LIMITED|LTD|INC|CORP|CORPORATION|PLC|UK)$/g, "");
  const aliases: Array<[RegExp, string]> = [
    [/^DIGIKEY(?:ELECTRONICS)?$/, "DIGIKEY"],
    [/^MOUSER(?:ELECTRONICS)?$/, "MOUSER"],
    [/^RS(?:COMPONENTS|ONLINE)?$/, "RS"],
    [/^RADWELL(?:INTERNATIONAL)?$/, "RADWELL"],
    [/^NEWARK$/, "NEWARK"],
    [/^FARNELL(?:ELEMENT14)?$/, "FARNELL"],
    [/^ELEMENT14(?:FARNELL)?$/, "FARNELL"],
    [/^TME(?:ELECTRONICCOMPONENTS)?$/, "TME"],
    [/^ARROW(?:ELECTRONICS)?$/, "ARROW"],
  ];
  return aliases.find(([pattern]) => pattern.test(normalized))?.[1] || normalized;
}

/**
 * Collapse direct and aggregator observations of the same retailer before
 * applying the two-independent-source identity rule.
 */
export function independentRetailerKey(input: {
  sourceId?: unknown;
  sourceName?: unknown;
  productUrl?: unknown;
}): string {
  const sourceName = normalizedRetailerName(input.sourceName);
  const sourceId = normalizedRetailerName(input.sourceId);
  const genericProviders = new Set(["OEMSECRETS", "WEB", "MARKETSCAN", "SUPPLIER"]);
  if (sourceName && !genericProviders.has(sourceName)) return `retailer:${sourceName}`;
  try {
    const url = new URL(String(input.productUrl || ""));
    const hostname = url.hostname.toLowerCase().replace(/^(?:www\.|m\.)/, "");
    if (hostname && !hostname.endsWith("oemsecrets.com")) return `host:${hostname}`;
  } catch {
    // Fall through to the provider key.
  }
  return sourceId ? `provider:${sourceId}` : "";
}
